import React from 'react';
import cn from 'classnames';
import {PreloaderIcon} from 'icons/svg';
import './style.scss';

// The ring of PreloaderIcon: radius 4.67 and stroke 1.33 in its 16×16 box.
const RING_RADIUS = 14 / 3;
const RING_STROKE = 4 / 3;
// In user units: older WebKit ignores pathLength on a <circle>.
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;
// An empty ring reads as "nothing is happening"; a sliver shows the run has begun.
const MIN_SHOWN_PROGRESS = 0.03;

/**
 * Figma `preloader-light-icon` (391:24202): the app's one loader — a three-quarter ring in
 * the current text colour, turning about its own centre. Every spinner is this component,
 * at the size its slot gives it; nothing draws a ring of its own.
 *
 * With a `progress` (0–1) the same ring stands still and fills clockwise from the top to that
 * share over a faint track — for waits whose progress is known.
 */
export const Preloader = ({size = 20, className, style, progress}: {
    size?: number; className?: string; style?: React.CSSProperties; progress?: number | null;
}) => {
    if (progress == null || !Number.isFinite(progress)) {
        return <PreloaderIcon className={cn('ff-spin', className)} width={size} height={size} style={style} aria-hidden/>;
    }
    const shown = Math.min(Math.max(progress, MIN_SHOWN_PROGRESS), 1);
    return (
        <svg className={cn('ff-progress-ring', className)} width={size} height={size} viewBox="0 0 16 16" fill="none"
             style={style} aria-hidden>
            <circle cx="8" cy="8" r={RING_RADIUS} stroke="currentColor" strokeWidth={RING_STROKE}
                    className="ff-progress-ring__track"/>
            <circle cx="8" cy="8" r={RING_RADIUS} stroke="currentColor" strokeWidth={RING_STROKE}
                    strokeDasharray={`${shown * RING_LENGTH} ${RING_LENGTH}`}
                    className="ff-progress-ring__fill"/>
        </svg>
    );
};

/** The whole page's loader — a quiet grey, not the text colour. */
export const PagePreloader = () => <Preloader size={40} style={{color: 'var(--onlight-text-tertiary)'}}/>;
